"use client";

import { useSearchParams } from "next/navigation";
import { JSX, useEffect, useRef, useState } from "react";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./TabbedContent.module.css";

type Item = {
  id: string | number;
  title: string;
  content: JSX.Element | Function;
  onClick?: () => void;
  disabled?: boolean;
};

type Props = {
  items: Item[];
  initialItemId?: string | number;
  hideContentPadding?: boolean;
  disableScrollOnTabChange?: boolean;
  freeGlobalScroll?: boolean;
  urlParamName?: string;
  keepMounted?: boolean;
};

export default function TabbedContent(props: Props) {
  const {
    items,
    initialItemId,
    hideContentPadding,
    freeGlobalScroll = true,
    disableScrollOnTabChange,
    urlParamName,
    keepMounted = false,
  } = props;

  const searchParams = useSearchParams();
  const tabFromUrl = urlParamName ? searchParams.get(urlParamName) : null;

  const [currentItemId, setCurrentItemId] = useState(
    items.find((item) => item.id.toString() === tabFromUrl)?.id ??
      initialItemId ??
      (items.length > 0 ? items[0].id : undefined),
  );

  const tabContentRef = useRef<HTMLDivElement | any>(null);
  const navTabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = navTabsRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;

      const atStart = el.scrollLeft === 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

      if (freeGlobalScroll && ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd))) {
        return;
      }

      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [freeGlobalScroll]);

  const currentItem = items.find((item) => item.id === currentItemId);

  useEffect(() => {
    const matchedItem = items.find((item) => item.id.toString() === tabFromUrl);
    if (matchedItem) {
      setCurrentItemId(matchedItem.id);
    }
  }, [tabFromUrl, items]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentItemId triggers the scroll intentionally
  useEffect(() => {
    if (!disableScrollOnTabChange) {
      if (tabContentRef.current) {
        tabContentRef.current.scrollTop = 0;
        window.scrollTo({ top: 0 });
      }
    }
  }, [disableScrollOnTabChange, currentItemId]);

  return (
    <div className={styles.container}>
      <div className={styles.navTabs} ref={navTabsRef}>
        {items.map((item) => (
          <button
            key={item.id}
            className={UStyles.classNames(
              styles.navLink,
              currentItemId === item.id ? styles.navLinkActive : "",
              item.disabled ? styles.navLinkDisabled : "",
            )}
            disabled={item.disabled}
            onClick={() => {
              if (currentItemId === item.id || item.disabled) {
                return;
              }

              setCurrentItemId(item.id);

              if (urlParamName && typeof window !== "undefined") {
                const url = new URL(window.location.href);
                url.searchParams.set(urlParamName, item.id.toString());
                window.history.replaceState(null, "", url.toString());
              }

              if (item.onClick) {
                item.onClick();
              }
            }}
          >
            {item.title}
          </button>
        ))}
      </div>

      <div
        className={UStyles.classNames(
          styles.tabContent,
          hideContentPadding ? styles.tabContentNoPadding : "",
        )}
        ref={tabContentRef}
      >
        {keepMounted
          ? items.map((item) => (
              <div
                key={item.id}
                style={item.id === currentItemId ? undefined : { display: "none" }}
              >
                {typeof item.content === "function" ? item.content() : item.content}
              </div>
            ))
          : currentItem?.content &&
            (typeof currentItem.content === "function"
              ? currentItem.content()
              : currentItem.content)}
      </div>
    </div>
  );
}
